import { useEffect } from "react";
import { useUpgradePrompt } from "@/state/UpgradePromptContext";
import { UpgradeModal } from "@/features/billing/UpgradeModal";
import { addUpgradeRequiredListener } from "@/lib/entitlementEvents";
import type { Feature } from "@/services/types";
import { REQUIRED_TIER, isProfessionalOption, planNameFor } from "@/features/billing/planFeatures";

/**
 * How each feature is named in the dialog's headline. The plan is appended from
 * {@link planNameFor} rather than written in, so a feature moving between tiers cannot leave
 * this copy quietly naming the wrong one — which it did for four of these when the multi-host
 * events and white-label moved to Team.
 */
const FEATURE_SUBJECT: Record<Feature, string> = {
  GROUP_EVENT: "Group events are",
  ROUND_ROBIN_EVENT: "Round Robin events are",
  COLLECTIVE_EVENT: "Collective events are",
  TEAMS: "Teams are",
  BOOKING_FORMS: "Booking forms are",
  EXPERIENCES: "Publishing booking experiences is",
  CUSTOM_BRANDING: "Your own logo, colours and name are",
  WHITE_LABEL: "Removing BunnyCal branding is",
};

function featureHeadline(feature: Feature): string {
  // White-label is included with Team but also sells as an option on Professional, so pointing
  // a solo host at a team plan would be wrong.
  if (isProfessionalOption(feature)) {
    return `${FEATURE_SUBJECT[feature]} available on Professional and included with Team.`;
  }
  return `${FEATURE_SUBJECT[feature]} a ${planNameFor(feature)} feature.`;
}

/**
 * Mounted once at the app root. Renders the single shared {@link UpgradeModal} and opens it
 * whenever any surface calls {@link useUpgradePrompt}.requestUpgrade(feature). Keeping exactly
 * one host avoids duplicate upgrade dialogs across the app.
 */
export function UpgradePromptHost() {
  const { open, feature, requestUpgrade, dismiss } = useUpgradePrompt();

  // Safety net: a plan-limit 403 from any API call (not pre-empted by a client check) opens
  // the same shared prompt. The raw error carries a code but usually not a feature, so the
  // dialog falls back to its generic copy.
  useEffect(
    () => addUpgradeRequiredListener((detail) => requestUpgrade(detail.feature ?? null)),
    [requestUpgrade],
  );

  const headline = feature ? featureHeadline(feature) : undefined;
  // Offer the plan that actually unlocks what they were blocked on; without this the modal
  // falls back to the catalog default, which is a Professional row.
  const requiredTier = feature ? REQUIRED_TIER[feature] : undefined;
  return (
    <UpgradeModal open={open} onClose={dismiss} headline={headline} requiredTier={requiredTier} />
  );
}
